import { useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';

import { BranchBadge } from '../../shared/GameElementPicker/BranchBadge';
import { GameElementIcon } from '../../shared/GameElementPicker/GameElementIcon';

import {
    GameElementDto,
    preferMainById,
    useGameElementsIncludingBranches,
} from '../../hooks/gameElements';

const WRAPPING_PATH_PREFIX = 'Gifts/Wrappings/GiftWrapIconic';

/**
 * Returns the subset of game elements that are gift-wrapping prefabs.
 * Filters by the well-known Unity asset path used by the Life client.
 */
export const useWrappingGameElements = () => {
    const query = useGameElementsIncludingBranches();
    const options = useMemo(
        () =>
            [...preferMainById(query.data?.gameElements ?? []).values()].filter(
                (el) => el.name?.includes(WRAPPING_PATH_PREFIX),
            ),
        [query.data],
    );
    return { options, isLoading: query.isLoading };
};

const wrappingLabel = (opt: GameElementDto): string => {
    const name = opt.name ?? opt.id;
    const file = name.split('/').pop() ?? name;
    return file.replace('.prefab', '');
};

interface Props {
    value: GameElementDto | null;
    onChange: (value: GameElementDto | null) => void;
    options: GameElementDto[];
    label?: string;
    margin?: 'none' | 'dense' | 'normal';
}

/**
 * Reusable Autocomplete picker for gift-wrapping game elements.
 * Shows a thumbnail + short prefab name for each option.
 */
export const WrappingElementPicker = ({
    value,
    onChange,
    options,
    label = 'Wrapping element',
    margin,
}: Props) => (
    <Autocomplete
        options={options}
        value={value}
        onChange={(_, val) => onChange(val)}
        getOptionLabel={wrappingLabel}
        renderOption={(props, opt) => (
            <li {...props} key={opt.id}>
                <GameElementIcon gameElementId={opt.id} size={24} />
                <span style={{ marginLeft: 8 }}>{wrappingLabel(opt)}</span>
                <BranchBadge element={opt} />
            </li>
        )}
        renderInput={(params) => (
            <TextField {...params} label={label} margin={margin} fullWidth />
        )}
    />
);
